import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Maximize2, Minus, Plus } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { SpecularButton } from "./specular-button.js";

/**
 * Infinite pan/zoom canvas on a dotted grid -- the viewport diagramming tools
 * (Figma, Miro, React Flow) use for anything bigger than its frame: org charts,
 * flow diagrams, seating maps.
 *
 * The children are laid out normally at their natural size inside a stage that
 * is translated and scaled as a whole, so nothing inside has to know about the
 * zoom. The dot grid is painted by the viewport, not the stage, so the dots
 * stay a crisp 1 device pixel at any zoom instead of blurring with the
 * transform; it tracks the content because its size and offset follow the same
 * zoom/pan values (see `.zoom-canvas` in styles/tokens.css).
 *
 * Interaction: drag empty space to pan (interactive elements keep their own
 * clicks), middle-drag anywhere to pan, ctrl/⌘ + wheel or pinch to zoom toward
 * the pointer, plain wheel to pan, +/-/0 and the arrow keys once focused, and
 * the floating controls bottom-right.
 */
const zoomCanvasVariants = cva(
  "zoom-canvas relative isolate h-full w-full touch-none overflow-hidden outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500 data-[panning=true]:cursor-grabbing",
  {
    variants: {
      /** `grab` advertises that the empty canvas is draggable. */
      cursor: {
        grab: "cursor-grab",
        default: "cursor-default",
      },
    },
    defaultVariants: { cursor: "grab" },
  }
);

/** Every default ZoomCanvas falls back to -- pass any at the call site to change it. */
export const zoomCanvasDefaults = {
  dots: "md" as ZoomCanvasDots,
  cursor: "grab" as "grab" | "default",
  minZoom: 0.25,
  maxZoom: 2.5,
  /** Multiplier applied per button press / keystroke. */
  zoomStep: 1.25,
  /** Breathing room left around the content by `fit()`, in px. */
  fitPadding: 48,
  /** How the view is placed the first time the content is measured. */
  initialView: "center" as "center" | "fit" | "none",
  /** Show the floating zoom cluster. */
  controls: true,
};

export interface ZoomCanvasHandle {
  zoomIn(): void;
  zoomOut(): void;
  /** Back to 100%, content centred horizontally at the top. */
  reset(): void;
  /** Scale the whole content into view (never past 100%) and centre it. */
  fit(): void;
  /** Pan -- without changing the zoom -- until `el` sits in the middle. */
  centerOn(el: Element | null | undefined): void;
  getZoom(): number;
}

export type ZoomCanvasDots = "none" | "sm" | "md" | "lg";

export interface ZoomCanvasProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onWheel">,
    VariantProps<typeof zoomCanvasVariants> {
  /**
   * Spacing of the background dot grid at 100% zoom. Driven by `data-dots` and
   * resolved in tokens.css rather than by a utility class, because a utility
   * setting `--zc-dot-gap` would lose the cascade to `.zoom-canvas`'s own
   * default for it.
   */
  dots?: ZoomCanvasDots;
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
  fitPadding?: number;
  initialView?: "center" | "fit" | "none";
  controls?: boolean;
  /** Fired after any zoom change, e.g. to mirror the level elsewhere. */
  onZoomChange?: (zoom: number) => void;
  /** Extra controls rendered left of the zoom cluster. */
  controlsSlot?: React.ReactNode;
  /** Classes for the stage that holds the children (e.g. padding). */
  contentClassName?: string;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Drags starting on these keep their own behaviour instead of panning. */
const NO_PAN = "button, a, input, select, textarea, [role='button'], [data-no-pan]";

const ZoomCanvas = React.forwardRef<ZoomCanvasHandle, ZoomCanvasProps>(
  (
    {
      className,
      contentClassName,
      dots = zoomCanvasDefaults.dots,

      cursor = zoomCanvasDefaults.cursor,
      minZoom = zoomCanvasDefaults.minZoom,
      maxZoom = zoomCanvasDefaults.maxZoom,
      zoomStep = zoomCanvasDefaults.zoomStep,
      fitPadding = zoomCanvasDefaults.fitPadding,
      initialView = zoomCanvasDefaults.initialView,
      controls = zoomCanvasDefaults.controls,
      onZoomChange,
      controlsSlot,
      children,
      ...rest
    },
    ref
  ) => {
    const viewportRef = React.useRef<HTMLDivElement>(null);
    const stageRef = React.useRef<HTMLDivElement>(null);

    const [zoom, setZoomState] = React.useState(1);
    const [pan, setPan] = React.useState({ x: 0, y: 0 });
    const [panning, setPanning] = React.useState(false);

    // Reading these inside the native wheel/pointer listeners rather than
    // closing over the state keeps the listeners registered once.
    const view = React.useRef({ zoom: 1, x: 0, y: 0 });
    view.current = { zoom, x: pan.x, y: pan.y };

    const onZoomChangeRef = React.useRef(onZoomChange);
    onZoomChangeRef.current = onZoomChange;

    const applyView = React.useCallback((z: number, x: number, y: number) => {
      view.current = { zoom: z, x, y };
      setZoomState(z);
      setPan({ x, y });
    }, []);

    const firstZoom = React.useRef(true);
    React.useEffect(() => {
      if (firstZoom.current) { firstZoom.current = false; return; }
      onZoomChangeRef.current?.(zoom);
    }, [zoom]);

    /** Zoom to `next` keeping the viewport point (cx, cy) under the pointer. */
    const zoomAt = React.useCallback(
      (next: number, cx: number, cy: number) => {
        const { zoom: z, x, y } = view.current;
        const nz = clamp(next, minZoom, maxZoom);
        if (nz === z) return;
        const k = nz / z;
        applyView(nz, cx - (cx - x) * k, cy - (cy - y) * k);
      },
      [applyView, minZoom, maxZoom]
    );

    const zoomByStep = React.useCallback(
      (factor: number) => {
        const vp = viewportRef.current;
        if (!vp) return;
        zoomAt(view.current.zoom * factor, vp.clientWidth / 2, vp.clientHeight / 2);
      },
      [zoomAt]
    );

    /** offsetWidth/Height are layout values, so they ignore the transform. */
    const measure = React.useCallback(() => {
      const vp = viewportRef.current;
      const stage = stageRef.current;
      if (!vp || !stage) return null;
      return {
        vw: vp.clientWidth,
        vh: vp.clientHeight,
        cw: stage.offsetWidth,
        ch: stage.offsetHeight,
      };
    }, []);

    const fit = React.useCallback(() => {
      const m = measure();
      if (!m || !m.cw || !m.ch) return;
      const z = clamp(
        Math.min((m.vw - fitPadding * 2) / m.cw, (m.vh - fitPadding * 2) / m.ch),
        minZoom,
        1 // never blow a small chart up past its natural size
      );
      applyView(z, (m.vw - m.cw * z) / 2, (m.vh - m.ch * z) / 2);
    }, [measure, applyView, fitPadding, minZoom]);

    const reset = React.useCallback(() => {
      const m = measure();
      if (!m) return;
      applyView(1, m.cw ? (m.vw - m.cw) / 2 : 0, fitPadding);
    }, [measure, applyView, fitPadding]);

    const centerOn = React.useCallback((el: Element | null | undefined) => {
      const vp = viewportRef.current;
      if (!vp || !el) return;
      const vr = vp.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      const dx = vr.left + vr.width / 2 - (er.left + er.width / 2);
      const dy = vr.top + vr.height / 2 - (er.top + er.height / 2);
      if (!dx && !dy) return;
      setPan(p => {
        const next = { x: p.x + dx, y: p.y + dy };
        view.current = { ...view.current, ...next };
        return next;
      });
    }, []);

    React.useImperativeHandle(
      ref,
      () => ({
        zoomIn: () => zoomByStep(zoomStep),
        zoomOut: () => zoomByStep(1 / zoomStep),
        reset,
        fit,
        centerOn,
        getZoom: () => view.current.zoom,
      }),
      [zoomByStep, zoomStep, reset, fit, centerOn]
    );

    // ── First layout ────────────────────────────────────────────────────────
    // Placed once, when the content first has a size; later expand/collapse
    // must not yank the view the user has set.
    const placed = React.useRef(initialView === "none");
    React.useEffect(() => {
      if (placed.current) return;
      const stage = stageRef.current;
      if (!stage) return;
      // The content is empty while the data loads, so the first real
      // measurement usually arrives from the observer rather than this pass.
      const ro = new ResizeObserver(() => place());
      const place = () => {
        const m = measure();
        if (!m || !m.cw || !m.ch) return;
        placed.current = true;
        ro.disconnect();
        if (initialView === "fit") fit();
        else reset();
      };
      place();
      if (!placed.current) ro.observe(stage);
      return () => ro.disconnect();
    }, [initialView, measure, fit, reset]);

    // ── Wheel: ctrl/⌘ or pinch zooms, plain wheel pans ──────────────────────
    React.useEffect(() => {
      const vp = viewportRef.current;
      if (!vp) return;
      const onWheel = (e: WheelEvent) => {
        e.preventDefault(); // non-passive, so the page behind never scrolls too
        const r = vp.getBoundingClientRect();
        if (e.ctrlKey || e.metaKey) {
          // Trackpad pinch arrives as ctrl+wheel; exp() keeps each notch a
          // constant ratio, so zooming in and back out lands where it started.
          zoomAt(view.current.zoom * Math.exp(-e.deltaY * 0.01), e.clientX - r.left, e.clientY - r.top);
          return;
        }
        const { zoom: z, x, y } = view.current;
        applyView(z, x - e.deltaX, y - e.deltaY);
      };
      vp.addEventListener("wheel", onWheel, { passive: false });
      return () => vp.removeEventListener("wheel", onWheel);
    }, [zoomAt, applyView]);

    // ── Pointer: drag to pan, two-finger pinch to zoom ──────────────────────
    const pointers = React.useRef(new Map<number, { x: number; y: number }>());
    const pinch = React.useRef<{ dist: number; cx: number; cy: number } | null>(null);
    /** Set once a drag travels far enough to be a pan rather than a click. */
    const dragged = React.useRef(false);

    const pointerPairs = () => [...pointers.current.values()];

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      const interactive = (e.target as HTMLElement).closest(NO_PAN);
      const isPanButton = e.button === 1 || (e.button === 0 && !interactive);
      if (e.pointerType === "mouse" && !isPanButton) return;
      if (e.pointerType !== "mouse" && interactive && pointers.current.size === 0) return;

      // Otherwise Windows starts its middle-click autoscroll on top of the pan.
      if (e.button === 1) e.preventDefault();
      dragged.current = false;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      // Capture is deferred until the drag passes the threshold below: while a
      // pointer is captured the follow-up `click` is retargeted to the capture
      // element, so capturing on every press would stop the cards' own buttons
      // and name links from ever receiving a plain click.
      if (pointers.current.size === 2) {
        e.currentTarget.setPointerCapture(e.pointerId);
        const [a, b] = pointerPairs() as [{ x: number; y: number }, { x: number; y: number }];
        pinch.current = {
          dist: Math.hypot(a.x - b.x, a.y - b.y),
          cx: (a.x + b.x) / 2,
          cy: (a.y + b.y) / 2,
        };
      }
      setPanning(true);
    };

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      const prev = pointers.current.get(e.pointerId);
      if (!prev) return;
      if (!dragged.current && (Math.abs(e.clientX - prev.x) > 2 || Math.abs(e.clientY - prev.y) > 2)) {
        dragged.current = true;
        // Now that it is a drag, capture so it keeps tracking outside the frame.
        e.currentTarget.setPointerCapture(e.pointerId);
      }
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.current.size >= 2 && pinch.current) {
        const [a, b] = pointerPairs() as [{ x: number; y: number }, { x: number; y: number }];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (!pinch.current.dist) return;
        const r = e.currentTarget.getBoundingClientRect();
        const cx = (a.x + b.x) / 2;
        const cy = (a.y + b.y) / 2;
        zoomAt(view.current.zoom * (dist / pinch.current.dist), cx - r.left, cy - r.top);
        pinch.current = { dist, cx, cy };
        return;
      }

      const { zoom: z, x, y } = view.current;
      applyView(z, x + (e.clientX - prev.x), y + (e.clientY - prev.y));
    };

    const endPointer = (e: React.PointerEvent<HTMLDivElement>) => {
      pointers.current.delete(e.pointerId);
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
      if (pointers.current.size < 2) pinch.current = null;
      if (pointers.current.size === 0) setPanning(false);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const nudge = e.shiftKey ? 160 : 60;
      const { zoom: z, x, y } = view.current;
      switch (e.key) {
        case "+": case "=": zoomByStep(zoomStep); break;
        case "-": case "_": zoomByStep(1 / zoomStep); break;
        case "0": reset(); break;
        case "ArrowLeft":  applyView(z, x + nudge, y); break;
        case "ArrowRight": applyView(z, x - nudge, y); break;
        case "ArrowUp":    applyView(z, x, y + nudge); break;
        case "ArrowDown":  applyView(z, x, y - nudge); break;
        default: return;
      }
      e.preventDefault();
    };

    return (
      <div
        ref={viewportRef}
        data-panning={panning ? "true" : undefined}
        data-dots={dots}
        tabIndex={0}
        role="application"
        aria-label="Pan and zoom canvas"
        // Zoom and pan are recomputed from the pointer on every wheel notch and
        // drag frame, so they cannot be static classes; the stage transform and
        // the dot grid's scale/offset both read them from tokens.css.
        style={{
          "--zc-zoom": zoom,
          "--zc-tx": `${pan.x}px`,
          "--zc-ty": `${pan.y}px`,
        } as React.CSSProperties}
        className={cn(zoomCanvasVariants({ cursor }), className)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onClickCapture={e => {
          // A pan that happened to start on a card must not also open it.
          if (!dragged.current) return;
          dragged.current = false;
          e.preventDefault();
          e.stopPropagation();
        }}
        onKeyDown={onKeyDown}
        {...rest}
      >
        <div ref={stageRef} className={cn("zoom-canvas-stage", contentClassName)}>
          {children}
        </div>

        {controls && (
          <div className="pointer-events-none absolute bottom-4 right-4 z-10 flex items-center gap-1.5">
            {controlsSlot}
            <div className="pointer-events-auto flex items-center gap-1 rounded-lg border border-surface-200 bg-surface-0/80 p-1 shadow-sm backdrop-blur-sm">
              <SpecularButton
                size="sm"
                surface="ghost"
                className="w-7 px-0"
                aria-label="Zoom out"
                disabled={zoom <= minZoom + 1e-4}
                onClick={() => zoomByStep(1 / zoomStep)}
              >
                <Minus size={14} />
              </SpecularButton>
              <SpecularButton
                size="sm"
                surface="ghost"
                className="w-14 px-0 tabular-nums"
                aria-label="Reset zoom to 100%"
                title="Reset zoom"
                onClick={reset}
              >
                {Math.round(zoom * 100)}%
              </SpecularButton>
              <SpecularButton
                size="sm"
                surface="ghost"
                className="w-7 px-0"
                aria-label="Zoom in"
                disabled={zoom >= maxZoom - 1e-4}
                onClick={() => zoomByStep(zoomStep)}
              >
                <Plus size={14} />
              </SpecularButton>
              <SpecularButton
                size="sm"
                surface="ghost"
                className="w-7 px-0"
                aria-label="Fit to screen"
                title="Fit to screen"
                onClick={fit}
              >
                <Maximize2 size={13} />
              </SpecularButton>
            </div>
          </div>
        )}
      </div>
    );
  }
);
ZoomCanvas.displayName = "ZoomCanvas";

export { ZoomCanvas, zoomCanvasVariants };
